import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApiClient } from '../api/client';
import { EXCHANGES } from '../constants/exchanges';
import { formatFundingAmount, formatFundingPnL, getFundingPct } from '../utils/stats';
import { useIsMobile } from '../hooks/useIsMobile';
import './HistoryPage.css';

const EXCHANGE_OPTIONS = EXCHANGES.map((e) => ({
  value: e,
  label: e.charAt(0).toUpperCase() + e.slice(1),
}));

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const TIME_RANGES = [
  { value: '1d', label: '1D' },
  { value: '1w', label: '1W' },
  { value: '1m', label: '1M' },
  { value: '1y', label: '1Y' },
  { value: 'all', label: 'ALL' },
];

function getRangeDates(range) {
  const now = new Date();
  const end = new Date(now);
  end.setUTCHours(23, 59, 59, 999);
  let start = new Date(now);
  const r = (range || '1w').toLowerCase();
  if (r === '1d') start.setDate(start.getDate() - 1);
  else if (r === '1w') start.setDate(start.getDate() - 7);
  else if (r === '1m') start.setDate(start.getDate() - 30);
  else if (r === '1y') start.setDate(start.getDate() - 365);
  else if (r === 'all') start.setDate(start.getDate() - 730);
  else start.setDate(start.getDate() - 7);
  start.setUTCHours(0, 0, 0, 0);
  return {
    startISO: start.toISOString().slice(0, 19).replace('T', 'T') + 'Z',
    endISO: end.toISOString().slice(0, 19).replace('T', 'T') + 'Z',
  };
}

/** Days in the selected range (for Funding % period/annualized in table). */
function getRangeDays(range) {
  const r = (range || '1w').toLowerCase();
  if (r === '1d') return 1;
  if (r === '1w') return 7;
  if (r === '1m') return 30;
  if (r === '1y') return 365;
  if (r === 'all') return 730;
  return 7;
}

/** Start and end timestamps (ms) for the selected range (for chart X domain). */
function getRangeMs(range) {
  const { startISO, endISO } = getRangeDates(range);
  return {
    startMs: new Date(startISO).getTime(),
    endMs: new Date(endISO).getTime(),
  };
}

function formatPaymentTime(isoStr) {
  if (!isoStr) return '–';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }) + ' GMT' + (d.getTimezoneOffset() <= 0 ? '+' : '') + (-d.getTimezoneOffset() / 60);
  } catch {
    return isoStr;
  }
}

const EXCHANGE_LABELS = { binance: 'Binance', bybit: 'Bybit', okx: 'OKX', hyperliquid: 'Hyperliquid', lighter: 'Lighter', extended: 'Extended' };

function formatMarketLabel(exchange, pair) {
  if (!exchange && !pair) return '–';
  const key = (exchange || '').toString().trim().toLowerCase();
  const label = EXCHANGE_LABELS[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '');
  const sym = (pair || '').toString().trim() || '–';
  if (!label) return sym;
  return `${label} • ${sym}`;
}

function FundingPaymentsTab({ onRefreshRef, positions }) {
  const { apiGet, apiFetch } = useApiClient();

  const [timeRange, setTimeRange] = useState('1w');
  const [selectedExchanges, setSelectedExchanges] = useState(() => EXCHANGE_OPTIONS.map((o) => o.value));
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [exchangeDropdownOpen, setExchangeDropdownOpen] = useState(false);
  const [symbolDropdownOpen, setSymbolDropdownOpen] = useState(false);
  const [aggregates, setAggregates] = useState({
    daily_totals: [],
    daily_totals_by_exchange: [],
    by_market: [],
    total_funding: 0,
  });
  const [aggregatesLoading, setAggregatesLoading] = useState(false);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartSeriesLoading, setChartSeriesLoading] = useState(false);
  const exchangeDropdownRef = useRef(null);
  const symbolDropdownRef = useRef(null);

  const availableSymbols = React.useMemo(() => {
    const pairs = [...new Set((aggregates.by_market || []).map((m) => m.pair).filter(Boolean))];
    return pairs.sort();
  }, [aggregates.by_market]);

  // Map (exchange, pair) -> { notional, opening_time, side } from positions for Funding % and long/short indicator.
  // Use loop key `ex` for exchange (not p.exchange) so Binance etc. always match; normalize pair (uppercase, no hyphens).
  const notionalByMarket = React.useMemo(() => {
    const map = {};
    if (!positions) return map;
    const exchanges = ['binance', 'bybit', 'okx', 'hyperliquid', 'lighter', 'extended'];
    const norm = (s) => (s || '').toString().toUpperCase().replace(/-/g, '');
    exchanges.forEach((ex) => {
      const list = positions[ex]?.positions || [];
      list.forEach((p) => {
        const pairNorm = norm(p.pair);
        if (!pairNorm) return;
        const key = `${ex}_${pairNorm}`;
        const notional = p.notional ?? (p.size * p.mark_price);
        if (notional != null) {
          const side = (p.side || '').toString().toLowerCase();
          map[key] = { notional, opening_time: p.opening_time, side: side === 'long' || side === 'short' ? side : '' };
        }
      });
    });
    return map;
  }, [positions]);

  const exchangeParam = selectedExchanges.length === EXCHANGE_OPTIONS.length ? '' : selectedExchanges.join(',');
  const symbolParam =
    selectedSymbols.length === 0 || selectedSymbols.length === availableSymbols.length
      ? ''
      : selectedSymbols.join(',');

  // Stable signature so chart effect only runs when open positions actually change (avoids refetch every 5s when positions object reference updates).
  const openMarketsSignature = React.useMemo(() => {
    const entries = Object.entries(notionalByMarket)
      .map(([k, v]) => [k, v?.opening_time, v?.notional].filter(Boolean))
      .sort((a, b) => (a[0] || '').localeCompare(b[0] || ''));
    return JSON.stringify(entries);
  }, [notionalByMarket]);

  const fetchAggregates = useCallback(async (opts) => {
    const background = opts?.background === true;
    if (!background) setAggregatesLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('range', timeRange);
      if (exchangeParam) params.set('exchange', exchangeParam);
      if (symbolParam) params.set('symbol', symbolParam);
      const json = await apiGet(`/api/history/funding-aggregates?${params.toString()}`);
      setAggregates({
        daily_totals: json.daily_totals || [],
        daily_totals_by_exchange: json.daily_totals_by_exchange || [],
        by_market: json.by_market || [],
        total_funding: json.total_funding ?? 0,
      });
    } catch {
      setAggregates({ daily_totals: [], daily_totals_by_exchange: [], by_market: [], total_funding: 0 });
    } finally {
      if (!background) setAggregatesLoading(false);
    }
  }, [timeRange, exchangeParam, symbolParam, apiGet]);

  const refreshAggregates = useCallback(() => {
    fetchAggregates({ background: false });
  }, [fetchAggregates]);

  useEffect(() => {
    if (onRefreshRef) onRefreshRef.current = refreshAggregates;
    return () => { if (onRefreshRef) onRefreshRef.current = null; };
  }, [onRefreshRef, refreshAggregates]);

  useEffect(() => {
    fetchAggregates({ background: true });
  }, [fetchAggregates]);

  const CHART_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
    '#f97316', '#6366f1', '#14b8a6', '#a855f7',
  ];

  useEffect(() => {
    const openKeys = Object.keys(notionalByMarket);
    if (openKeys.length === 0) {
      setChartSeries([]);
      return;
    }
    const openEntries = openKeys.map((k) => ({ key: k, ...notionalByMarket[k] }));
    const oldestOpening = openEntries.reduce((min, e) => {
      const t = e.opening_time ? new Date(e.opening_time).getTime() : 0;
      return t && (min === null || t < min) ? t : min;
    }, null);
    if (oldestOpening == null) {
      setChartSeries([]);
      return;
    }
    const startISO = new Date(oldestOpening).toISOString().slice(0, 19).replace('T', 'T') + 'Z';
    const endISO = new Date().toISOString().slice(0, 19).replace('T', 'T') + 'Z';
    setChartSeriesLoading(true);
    apiGet(`/api/history/funding-payments/export?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`)
      .then((res) => {
        const items = res.items || [];
        const byKey = {};
        items.forEach((item) => {
          const ex = (item.exchange || '').toString().toLowerCase();
          const pair = (item.pair || '').toString().toUpperCase().replace(/-/g, '');
          const key = `${ex}_${pair}`;
          if (!notionalByMarket[key]) return;
          if (!byKey[key]) byKey[key] = [];
          byKey[key].push({ payment_time: item.payment_time, amount: Number(item.amount) || 0 });
        });
        const MIN_DAYS = 1 / 24;
        const series = [];
        openKeys.forEach((key, idx) => {
          const info = notionalByMarket[key];
          if (!info?.notional || !info?.opening_time) return;
          const openingMs = new Date(info.opening_time).getTime();
          const payments = (byKey[key] || [])
            .filter((p) => new Date(p.payment_time).getTime() >= openingMs)
            .sort((a, b) => new Date(a.payment_time).getTime() - new Date(b.payment_time).getTime());
          const points = [{ x: openingMs, y: 0 }];
          let cumulative = 0;
          payments.forEach((p) => {
            cumulative += p.amount;
            const t = new Date(p.payment_time).getTime();
            const days = Math.max(MIN_DAYS, (t - openingMs) / (24 * 60 * 60 * 1000));
            const annualizedPct = (cumulative / info.notional) * 100 * (365 / days);
            points.push({ x: t, y: annualizedPct });
          });
          if (points.length === 1) points.push({ x: Date.now(), y: points[0].y });
          const [ex, pair] = key.split('_');
          const label = formatMarketLabel(ex, pair);
          const side = info.side === 'long' || info.side === 'short' ? info.side : '';
          series.push({
            key,
            label,
            side,
            color: CHART_COLORS[idx % CHART_COLORS.length],
            points,
          });
        });
        setChartSeries(series);
      })
      .catch(() => setChartSeries([]))
      .finally(() => setChartSeriesLoading(false));
    // Intentionally depend only on openMarketsSignature so we don't refetch every time positions object reference updates (e.g. every 5s).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMarketsSignature, apiGet]);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (exchangeDropdownRef.current && !exchangeDropdownRef.current.contains(e.target)) setExchangeDropdownOpen(false);
      if (symbolDropdownRef.current && !symbolDropdownRef.current.contains(e.target)) setSymbolDropdownOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const { startMs: rangeStartMs, endMs: rangeEndMs } = getRangeMs(timeRange);
  const chartSeriesFiltered = React.useMemo(() => {
    return chartSeries.map((s) => {
      let points = s.points.filter((p) => p.x >= rangeStartMs && p.x <= rangeEndMs);
      const beforeRange = s.points.filter((p) => p.x < rangeStartMs);
      if (beforeRange.length > 0 && points.length > 0) {
        const lastBefore = beforeRange[beforeRange.length - 1];
        points = [{ x: rangeStartMs, y: lastBefore.y }, ...points];
      } else if (beforeRange.length > 0 && points.length === 0) {
        const lastBefore = beforeRange[beforeRange.length - 1];
        points = [
          { x: rangeStartMs, y: lastBefore.y },
          { x: rangeEndMs, y: lastBefore.y },
        ];
      }
      points.sort((a, b) => a.x - b.x);
      return { ...s, points };
    }).filter((s) => s.points.length > 0);
  }, [chartSeries, timeRange]);

  const dates = aggregates.daily_totals?.map((d) => d.date) || [];
  const { daySums, zeroLineTopPct, posRegionPct, negRegionPct, maxPos, maxNeg } = React.useMemo(() => {
    let maxPosVal = 0;
    let maxNegVal = 0;
    const sums = {};
    dates.forEach((date) => {
      const d = aggregates.daily_totals?.find((x) => x.date === date);
      const total = d ? d.total : 0;
      const pos = total > 0 ? total : 0;
      const neg = total < 0 ? total : 0;
      sums[date] = { pos, neg, total };
      if (pos > maxPosVal) maxPosVal = pos;
      if (Math.abs(neg) > maxNegVal) maxNegVal = Math.abs(neg);
    });
    const totalRange = maxPosVal + maxNegVal;
    const safeTotal = Math.max(totalRange, 0.01);
    // Zero line from top (%): positive region height; ensure negative region has at least 15% for labels
    const MIN_NEG_REGION_PCT = 15;
    const rawZeroTop = totalRange > 0 ? (100 * maxPosVal) / safeTotal : 50;
    const zeroLineTopPctVal = Math.min(rawZeroTop, 100 - MIN_NEG_REGION_PCT);
    const posRegionPctVal = zeroLineTopPctVal;
    const negRegionPctVal = 100 - zeroLineTopPctVal;
    return {
      daySums: sums,
      zeroLineTopPct: zeroLineTopPctVal,
      posRegionPct: posRegionPctVal,
      negRegionPct: negRegionPctVal,
      maxPos: Math.max(maxPosVal, 0.01),
      maxNeg: Math.max(maxNegVal, 0.01),
    };
  }, [dates, aggregates.daily_totals]);

  return (
    <div className="history-tab funding-payments-tab">
      {/* Total Funding by Period and Markets (Extended-style) */}
      <section className="history-aggregates">
        <div className="history-aggregates-header">
          <h3 className="history-aggregates-title">Total Funding by Period and Markets</h3>
          <div className="history-aggregates-controls">
            <div className="history-time-range">
              {TIME_RANGES.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`history-time-btn ${timeRange === value ? 'active' : ''}`}
                  onClick={() => setTimeRange(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="history-aggregates-market">
              <div className="history-multiselect-wrap" ref={exchangeDropdownRef}>
                <button
                  type="button"
                  className="history-multiselect-trigger"
                  onClick={() => { setExchangeDropdownOpen((o) => !o); setSymbolDropdownOpen(false); }}
                  aria-expanded={exchangeDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span>
                    {selectedExchanges.length === EXCHANGE_OPTIONS.length
                      ? 'All Exchanges'
                      : `${selectedExchanges.length} Exchange${selectedExchanges.length !== 1 ? 's' : ''}`}
                  </span>
                  <span className="history-multiselect-chevron" aria-hidden>▼</span>
                </button>
                {exchangeDropdownOpen && (
                  <div className="history-multiselect-panel" role="listbox">
                    <label className="history-multiselect-option">
                      <input
                        type="checkbox"
                        checked={selectedExchanges.length === EXCHANGE_OPTIONS.length}
                        onChange={(e) => {
                          setSelectedExchanges(e.target.checked ? EXCHANGE_OPTIONS.map((o) => o.value) : []);
                        }}
                      />
                      <span>Select all</span>
                    </label>
                    {EXCHANGE_OPTIONS.map((opt) => (
                      <label key={opt.value} className="history-multiselect-option">
                        <input
                          type="checkbox"
                          checked={selectedExchanges.includes(opt.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedExchanges((prev) => [...prev, opt.value].sort());
                            } else {
                              setSelectedExchanges((prev) => prev.filter((x) => x !== opt.value));
                            }
                          }}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="history-multiselect-wrap" ref={symbolDropdownRef}>
                <button
                  type="button"
                  className="history-multiselect-trigger"
                  onClick={() => { setSymbolDropdownOpen((o) => !o); setExchangeDropdownOpen(false); }}
                  aria-expanded={symbolDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span>
                    {selectedSymbols.length === 0 || selectedSymbols.length === availableSymbols.length
                      ? 'All symbols'
                      : `${selectedSymbols.length} symbol${selectedSymbols.length !== 1 ? 's' : ''}`}
                  </span>
                  <span className="history-multiselect-chevron" aria-hidden>▼</span>
                </button>
                {symbolDropdownOpen && (
                  <div className="history-multiselect-panel history-multiselect-panel-symbols" role="listbox">
                    <label className="history-multiselect-option">
                      <input
                        type="checkbox"
                        checked={selectedSymbols.length === 0 || selectedSymbols.length === availableSymbols.length}
                        onChange={(e) => {
                          setSelectedSymbols(e.target.checked ? [] : [...availableSymbols]);
                        }}
                      />
                      <span>Select all</span>
                    </label>
                    {availableSymbols.length === 0 ? (
                      <div className="history-multiselect-empty">No symbols in data</div>
                    ) : (
                      availableSymbols.map((sym) => {
                        const isChecked = selectedSymbols.length === 0 || selectedSymbols.includes(sym);
                        return (
                          <label key={sym} className="history-multiselect-option">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSymbols((prev) => {
                                    const next = [...(prev.length === 0 ? availableSymbols : prev), sym].filter((x, i, a) => a.indexOf(x) === i).sort();
                                    return next.length === availableSymbols.length ? [] : next;
                                  });
                                } else {
                                  setSelectedSymbols((prev) =>
                                    prev.length === 0 ? availableSymbols.filter((x) => x !== sym) : prev.filter((x) => x !== sym)
                                  );
                                }
                              }}
                            />
                            <span>{sym}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="history-aggregates-body">
          <div className="history-chart-wrap">
            {aggregatesLoading ? (
              <div className="history-chart-loading">Loading…</div>
            ) : dates.length === 0 ? (
              <div className="history-chart-empty">No data for selected range</div>
            ) : (
              <div className="history-chart-bars">
                {dates.map((date) => {
                  const { pos, neg, total: dayNet } = daySums[date] || { pos: 0, neg: 0, total: 0 };
                  const posHeightPct = (pos / maxPos) * posRegionPct;
                  const negHeightPct = (Math.abs(neg) / maxNeg) * negRegionPct;
                  const isPositive = dayNet >= 0;
                  const zeroLineBottomPct = 100 - zeroLineTopPct;
                  return (
                    <div key={date} className="history-chart-bar-group">
                      <div className="history-chart-column" title={`${date}: ${formatFundingPnL(dayNet)}`}>
                        <div
                          className="history-chart-zero-line"
                          aria-hidden
                          style={{ top: `${zeroLineTopPct}%` }}
                        />
                        {posHeightPct > 0 && (
                          <>
                            <div
                              className="history-chart-bar-absolute history-chart-bar-positive"
                              style={{
                                bottom: `${zeroLineBottomPct}%`,
                                height: `${posHeightPct}%`,
                              }}
                            />
                            {isPositive && (
                              <span
                                className="history-chart-bar-value history-chart-value-floating history-chart-value-above"
                                style={{ bottom: `calc(${zeroLineBottomPct}% + ${posHeightPct}% + 4px)` }}
                              >
                                {formatFundingAmount(dayNet)}
                              </span>
                            )}
                          </>
                        )}
                        {negHeightPct > 0 && (
                          <>
                            <div
                              className="history-chart-bar-absolute history-chart-bar-negative"
                              style={{
                                top: `${zeroLineTopPct}%`,
                                height: `${negHeightPct}%`,
                              }}
                            />
                            {!isPositive && (
                              <span
                                className="history-chart-bar-value history-chart-value-floating history-chart-value-below"
                                style={{ top: `calc(${zeroLineTopPct}% + ${negHeightPct}% + 4px)` }}
                              >
                                {formatFundingAmount(dayNet)}
                              </span>
                            )}
                          </>
                        )}
                        {dayNet === 0 && (
                          <span
                            className="history-chart-bar-value history-chart-value-floating"
                            style={{ bottom: `calc(${zeroLineBottomPct}% + 4px)` }}
                          >
                            –
                          </span>
                        )}
                      </div>
                      <span className="history-chart-bar-label">
                        {new Date(date + 'Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="history-net-by-market">
            <table className="history-net-table">
              <thead>
                <tr>
                  <th title="Exchange and symbol (long/short indicator)">Market</th>
                  <th title="First payment date in selected range">Since</th>
                  <th title="Net funding payment in selected range">Net FP</th>
                  <th title="Period % and annualized % for selected range">Funding %</th>
                </tr>
              </thead>
              <tbody>
                {aggregates.by_market.length === 0 ? (
                  <tr><td colSpan={4} className="history-empty">No market data for selected filters</td></tr>
                ) : (
                  <>
                    {(() => {
                      const rangeDays = getRangeDays(timeRange);
                      const openMarkets = aggregates.by_market.filter((m) => {
                        const exNorm = (m.exchange || '').toString().toLowerCase();
                        const pairNorm = (m.pair || '').toString().toUpperCase().replace(/-/g, '');
                        const key = `${exNorm}_${pairNorm}`;
                        const info = notionalByMarket[key];
                        const notional = info?.notional;
                        return getFundingPct(m.net_funding, notional, rangeDays) != null;
                      });
                      const totalOpenFunding = openMarkets.reduce((sum, m) => sum + (m.net_funding || 0), 0);
                      return (
                        <>
                          {openMarkets.length === 0 ? (
                            <tr><td colSpan={4} className="history-empty">No positions in selected range</td></tr>
                          ) : (
                            openMarkets.map((m) => {
                              const exNorm = (m.exchange || '').toString().toLowerCase();
                              const pairNorm = (m.pair || '').toString().toUpperCase().replace(/-/g, '');
                              const key = `${exNorm}_${pairNorm}`;
                              const info = notionalByMarket[key];
                              const notional = info?.notional;
                              const pct = getFundingPct(m.net_funding, notional, rangeDays);
                              const pctStr = pct != null
                                ? `${pct.periodPct >= 0 ? '+' : ''}${pct.periodPct.toFixed(2)}%/${pct.annualizedPct >= 0 ? '+' : ''}${pct.annualizedPct.toFixed(1)}%`
                                : '–';
                              const sideClass = info?.side === 'long' || info?.side === 'short' ? info.side : '';
                              const marketLabel = formatMarketLabel(m.exchange, m.pair);
                              const marketTitle = info?.side ? `${marketLabel} (${info.side})` : marketLabel;
                              const sinceTitle = m.since ? `First payment in range: ${m.since}` : '–';
                              const netFpTitle = `Net funding in selected range (${timeRange.toUpperCase()}): ${formatFundingPnL(m.net_funding)}`;
                              const pctTitle = pct != null
                                ? `Period: ${pct.periodPct >= 0 ? '+' : ''}${pct.periodPct.toFixed(2)}%, Annualized: ${pct.annualizedPct >= 0 ? '+' : ''}${pct.annualizedPct.toFixed(1)}% (${rangeDays} days)`
                                : '–';
                              return (
                                <tr key={`${m.exchange}-${m.pair}`}>
                                  <td className="history-market-cell" title={marketTitle}>
                                    {sideClass ? <span className={`history-side-indicator ${sideClass}`} aria-hidden /> : null}
                                    {marketLabel}
                                  </td>
                                  <td className="history-since" title={sinceTitle}>{m.since ?? '–'}</td>
                                  <td className={m.net_funding >= 0 ? 'positive' : 'negative'} title={netFpTitle}>
                                    {formatFundingPnL(m.net_funding)}
                                  </td>
                                  <td className="history-funding-pct-cell" title={pctTitle}>
                                    {pctStr}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                          <tr className="history-net-total-row">
                            <td colSpan={2} title="Total net funding in selected range">Total</td>
                            <td className={totalOpenFunding >= 0 ? 'positive' : 'negative'} title={formatFundingPnL(totalOpenFunding)}>
                              {formatFundingPnL(totalOpenFunding)}
                            </td>
                            <td className="history-funding-pct-cell">–</td>
                          </tr>
                        </>
                      );
                    })()}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Annualized funding % by instrument (filtered by selected range) */}
      <section className="history-annualized-chart-section">
        <h3 className="history-aggregates-title">Annualized funding % by instrument</h3>
        {chartSeriesLoading ? (
          <div className="history-chart-loading">Loading…</div>
        ) : chartSeries.length === 0 ? (
          <div className="history-chart-empty">No open positions</div>
        ) : (
          <>
            <AnnualizedFundingChart series={chartSeriesFiltered} />
            <div className="history-annualized-legend">
              {chartSeries.map((s) => (
                <div key={s.key} className="history-annualized-legend-item">
                  <span className="history-annualized-legend-swatch" style={{ backgroundColor: s.color }} aria-hidden />
                  <span className="history-annualized-legend-label">{s.label}</span>
                  {s.side && <span className={`history-annualized-legend-side ${s.side}`}>({s.side})</span>}
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function interpolateYAtX(points, x) {
  if (!points.length) return null;
  if (points.length === 1) return points[0].x <= x ? points[0].y : null;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (x >= a.x && x <= b.x) {
      const t = (x - a.x) / (b.x - a.x || 1);
      return a.y + t * (b.y - a.y);
    }
  }
  return x < points[0].x ? points[0].y : points[points.length - 1].y;
}

function AnnualizedFundingChart({ series }) {
  const width = 900;
  const height = 280;
  const padding = { top: 12, right: 12, bottom: 24, left: 52 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const allPoints = series.flatMap((s) => s.points);
  if (allPoints.length === 0) return null;
  const xMin = Math.min(...allPoints.map((p) => p.x));
  const xMax = Math.max(...allPoints.map((p) => p.x));
  const yExtent = allPoints.reduce(
    (acc, p) => [Math.min(acc[0], p.y), Math.max(acc[1], p.y)],
    [0, 0]
  );
  const yPadding = Math.max((yExtent[1] - yExtent[0]) * 0.1, 0.5);
  const yMin = Math.min(-10, 0, yExtent[0]) - yPadding;
  const yMax = Math.max(10, 0, yExtent[1]) + yPadding;
  const xRange = xMax - xMin || 1;
  const yRange = yMax - yMin || 1;

  const toX = (x) => padding.left + ((x - xMin) / xRange) * innerWidth;
  const toY = (y) => padding.top + innerHeight - ((y - yMin) / yRange) * innerHeight;
  const fromX = (viewX) => xMin + (viewX - padding.left) / innerWidth * xRange;

  const zeroY = toY(0);
  const pathD = (points) =>
    points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(p.x)} ${toY(p.y)}`).join(' ');

  const yTicks = [0];
  if (yMin < 0) yTicks.push(yMin);
  if (yMax > 0) yTicks.push(yMax);
  if (yMin < -10 && yMax > -10) yTicks.push(-10);
  if (yMin < 10 && yMax > 10) yTicks.push(10);
  const uniqYTicks = [...new Set(yTicks)].sort((a, b) => a - b);

  const xTickCount = 5;
  const xTicks = [];
  for (let i = 0; i <= xTickCount; i++) {
    const t = xMin + (xRange * i) / xTickCount;
    xTicks.push(t);
  }

  const formatXLabel = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
  };

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const scaleX = width / rect.width;
    const viewX = (e.clientX - rect.left) * scaleX;
    if (viewX < padding.left || viewX > width - padding.right) {
      setTooltip(null);
      return;
    }
    const dataX = fromX(viewX);
    const dateStr = new Date(dataX).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const values = series.map((s) => {
      const y = interpolateYAtX(s.points, dataX);
      return y != null ? { label: s.label, side: s.side, value: y, color: s.color } : null;
    }).filter(Boolean);
    setTooltip({ clientX: e.clientX, clientY: e.clientY, dateStr, values, viewX });
  };

  const handleMouseLeave = () => setTooltip(null);

  return (
    <div className="history-annualized-chart-wrap" onMouseLeave={handleMouseLeave}>
      <svg
        ref={svgRef}
        className="history-annualized-chart-svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={handleMouseMove}
      >
        <line x1={padding.left} x2={width - padding.right} y1={zeroY} y2={zeroY} className="history-annualized-zero-line" stroke="#555" strokeWidth="1" strokeDasharray="4,2" />
        <line x1={padding.left} x2={width - padding.right} y1={toY(10)} y2={toY(10)} className="history-annualized-ref-line" stroke="#666" strokeWidth="1" strokeDasharray="4,2" />
        <line x1={padding.left} x2={width - padding.right} y1={toY(-10)} y2={toY(-10)} className="history-annualized-ref-line" stroke="#666" strokeWidth="1" strokeDasharray="4,2" />
        {uniqYTicks.map((v) => (
          <text key={v} x={padding.left - 6} y={toY(v) + 4} textAnchor="end" className="history-annualized-axis-label" fill="#888" fontSize="10">{v.toFixed(2)}%</text>
        ))}
        {xTicks.map((t) => (
          <text key={t} x={toX(t)} y={height - 6} textAnchor="middle" className="history-annualized-axis-label" fill="#888" fontSize="10">{formatXLabel(t)}</text>
        ))}
        {tooltip && (
          <line x1={tooltip.viewX} x2={tooltip.viewX} y1={padding.top} y2={height - padding.bottom} className="history-annualized-crosshair" stroke="#888" strokeWidth="1" strokeDasharray="2,2" />
        )}
        {series.map((s) => (
          <path key={s.key} d={pathD(s.points)} fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        ))}
      </svg>
      {tooltip && tooltip.values.length > 0 && (
        <div
          className="history-annualized-tooltip"
          style={{
            left: tooltip.clientX + 12,
            top: tooltip.clientY + 12,
          }}
        >
          <div className="history-annualized-tooltip-date">{tooltip.dateStr}</div>
          {tooltip.values.map((v) => (
            <div key={v.label} className="history-annualized-tooltip-row">
              <span className="history-annualized-tooltip-swatch" style={{ backgroundColor: v.color }} />
              <span>{v.label}{v.side ? ` (${v.side})` : ''}</span>
              <span className="history-annualized-tooltip-value">{v.value >= 0 ? '+' : ''}{v.value.toFixed(2)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FundingPaymentHistoryTab({ onRefreshRef, positions }) {
  const { apiGet, apiFetch } = useApiClient();
  const [timeRange, setTimeRange] = useState('1w');
  const [selectedExchanges, setSelectedExchanges] = useState(() => EXCHANGE_OPTIONS.map((o) => o.value));
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [exchangeDropdownOpen, setExchangeDropdownOpen] = useState(false);
  const [symbolDropdownOpen, setSymbolDropdownOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0 });
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const exchangeDropdownRef = useRef(null);
  const symbolDropdownRef = useRef(null);

  const availableSymbols = React.useMemo(() => {
    const pairs = new Set();
    if (!positions) return [];
    EXCHANGES.forEach((ex) => {
      (positions[ex]?.positions || []).forEach((p) => {
        const pair = (p.pair || '').toString().toUpperCase().replace(/-/g, '');
        if (pair) pairs.add(pair);
      });
    });
    return [...pairs].sort();
  }, [positions]);

  const exchangeParam = selectedExchanges.length === EXCHANGE_OPTIONS.length ? '' : selectedExchanges.join(',');
  const symbolParam =
    selectedSymbols.length === 0 || selectedSymbols.length === availableSymbols.length
      ? ''
      : selectedSymbols.join(',');

  const syncFundingPayments = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/positions/sync-funding-payments', { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || res.statusText || 'Sync failed');
      }
      return await res.json();
    } catch (e) {
      console.warn('Funding sync failed:', e.message);
      throw e;
    }
  }, [apiFetch]);

  const fetchPayments = useCallback(async (opts) => {
    const background = opts?.background === true;
    if (!background) setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (exchangeParam) params.set('exchange', exchangeParam);
      if (symbolParam) params.set('symbol', symbolParam);
      const rangeDates = getRangeDates(timeRange);
      params.set('start', startDate ? (startDate + (startDate.length <= 10 ? 'T00:00:00Z' : '')) : rangeDates.startISO);
      params.set('end', endDate ? (endDate + (endDate.length <= 10 ? 'T23:59:59Z' : '')) : rangeDates.endISO);
      params.set('page', String(page));
      params.set('per_page', String(perPage));
      const json = await apiGet(`/api/history/funding-payments?${params.toString()}`);
      setData({
        items: json.items || [],
        total: json.total ?? 0,
        total_pages: json.total_pages ?? 0,
      });
    } catch (e) {
      setError(e.message || 'Failed to load payment history');
      setData({ items: [], total: 0, total_pages: 0 });
    } finally {
      if (!background) setLoading(false);
    }
  }, [exchangeParam, symbolParam, startDate, endDate, timeRange, page, perPage, apiGet]);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await syncFundingPayments();
      await fetchPayments();
    } catch (e) {
      setError(e.message || 'Refresh failed');
    } finally {
      setLoading(false);
    }
  }, [syncFundingPayments, fetchPayments]);

  useEffect(() => {
    if (onRefreshRef) onRefreshRef.current = handleRefresh;
    return () => { if (onRefreshRef) onRefreshRef.current = null; };
  }, [onRefreshRef, handleRefresh]);

  useEffect(() => {
    fetchPayments({ background: true });
  }, [fetchPayments]);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (exchangeDropdownRef.current && !exchangeDropdownRef.current.contains(e.target)) setExchangeDropdownOpen(false);
      if (symbolDropdownRef.current && !symbolDropdownRef.current.contains(e.target)) setSymbolDropdownOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const formatCsvDate = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      const day = String(d.getUTCDate()).padStart(2, '0');
      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
      const year = d.getUTCFullYear();
      const hour = String(d.getUTCHours()).padStart(2, '0');
      const min = String(d.getUTCMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hour}:${min}`;
    } catch {
      return String(isoStr);
    }
  };

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const rangeDates = getRangeDates(timeRange);
      const params = new URLSearchParams();
      if (exchangeParam) params.set('exchange', exchangeParam);
      if (symbolParam) params.set('symbol', symbolParam);
      params.set('start', startDate ? (startDate + (startDate.length <= 10 ? 'T00:00:00Z' : '')) : rangeDates.startISO);
      params.set('end', endDate ? (endDate + (endDate.length <= 10 ? 'T23:59:59Z' : '')) : rangeDates.endISO);
      const json = await apiGet(`/api/history/funding-payments/export?${params.toString()}`);
      const items = json.items || [];
      const headers = ['Date/Time', 'Exchange', 'Market', 'Side', 'Position Size', 'Payment', 'Rate'];
      const rows = items.map((r) => [
        formatCsvDate(r.payment_time),
        r.exchange ? r.exchange.toUpperCase() : '',
        r.pair || '',
        r.side || '',
        r.position_size != null && r.position_size !== '' ? String(r.position_size) : '',
        r.amount != null ? formatFundingAmount(r.amount) : '',
        r.rate != null ? `${(Number(r.rate) * 100).toFixed(4)}%` : '',
      ]);
      const csv = [headers.join(','), ...rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `funding-payments-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error('Export failed:', e);
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [timeRange, startDate, endDate, exchangeParam, symbolParam, apiGet]);

  const totalPages = data.total_pages || Math.max(1, Math.ceil(data.total / perPage));

  return (
    <div className="history-tab funding-payment-history-tab">
      <section className="history-aggregates history-payment-history-filters">
        <div className="history-aggregates-header">
          <h3 className="history-aggregates-title">Filters</h3>
          <div className="history-aggregates-controls">
            <div className="history-time-range">
              {TIME_RANGES.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`history-time-btn ${timeRange === value ? 'active' : ''}`}
                  onClick={() => setTimeRange(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="history-aggregates-market">
              <div className="history-multiselect-wrap" ref={exchangeDropdownRef}>
                <button
                  type="button"
                  className="history-multiselect-trigger"
                  onClick={() => { setExchangeDropdownOpen((o) => !o); setSymbolDropdownOpen(false); }}
                  aria-expanded={exchangeDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span>
                    {selectedExchanges.length === EXCHANGE_OPTIONS.length ? 'All Exchanges' : `${selectedExchanges.length} Exchange${selectedExchanges.length !== 1 ? 's' : ''}`}
                  </span>
                  <span className="history-multiselect-chevron" aria-hidden>▼</span>
                </button>
                {exchangeDropdownOpen && (
                  <div className="history-multiselect-panel" role="listbox">
                    <label className="history-multiselect-option">
                      <input
                        type="checkbox"
                        checked={selectedExchanges.length === EXCHANGE_OPTIONS.length}
                        onChange={(e) => { setSelectedExchanges(e.target.checked ? EXCHANGE_OPTIONS.map((o) => o.value) : []); setPage(1); }}
                      />
                      <span>Select all</span>
                    </label>
                    {EXCHANGE_OPTIONS.map((opt) => (
                      <label key={opt.value} className="history-multiselect-option">
                        <input
                          type="checkbox"
                          checked={selectedExchanges.includes(opt.value)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedExchanges((prev) => [...prev, opt.value].sort());
                            else setSelectedExchanges((prev) => prev.filter((x) => x !== opt.value));
                            setPage(1);
                          }}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="history-multiselect-wrap" ref={symbolDropdownRef}>
                <button
                  type="button"
                  className="history-multiselect-trigger"
                  onClick={() => { setSymbolDropdownOpen((o) => !o); setExchangeDropdownOpen(false); }}
                  aria-expanded={symbolDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span>
                    {selectedSymbols.length === 0 || selectedSymbols.length === availableSymbols.length ? 'All symbols' : `${selectedSymbols.length} symbol${selectedSymbols.length !== 1 ? 's' : ''}`}
                  </span>
                  <span className="history-multiselect-chevron" aria-hidden>▼</span>
                </button>
                {symbolDropdownOpen && (
                  <div className="history-multiselect-panel history-multiselect-panel-symbols" role="listbox">
                    <label className="history-multiselect-option">
                      <input
                        type="checkbox"
                        checked={selectedSymbols.length === 0 || selectedSymbols.length === availableSymbols.length}
                        onChange={(e) => { setSelectedSymbols(e.target.checked ? [] : [...availableSymbols]); setPage(1); }}
                      />
                      <span>Select all</span>
                    </label>
                    {availableSymbols.length === 0 ? (
                      <div className="history-multiselect-empty">No symbols in data</div>
                    ) : (
                      availableSymbols.map((sym) => {
                        const isChecked = selectedSymbols.length === 0 || selectedSymbols.includes(sym);
                        return (
                          <label key={sym} className="history-multiselect-option">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSymbols((prev) => {
                                    const next = [...(prev.length === 0 ? availableSymbols : prev), sym].filter((x, i, a) => a.indexOf(x) === i).sort();
                                    return next.length === availableSymbols.length ? [] : next;
                                  });
                                } else {
                                  setSelectedSymbols((prev) => prev.length === 0 ? availableSymbols.filter((x) => x !== sym) : prev.filter((x) => x !== sym));
                                }
                                setPage(1);
                              }}
                            />
                            <span>{sym}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="history-toolbar">
        <div className="history-toolbar-per-page">
          <label htmlFor="history-per-page-history">Per page</label>
          <select id="history-per-page-history" value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }} className="history-select">
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <button type="button" className="history-btn history-btn-export history-btn-export-desktop-only" onClick={exportCsv} disabled={exporting || data.total === 0}>
          <span className="history-btn-icon" aria-hidden>↓</span> {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
        <button type="button" className="history-btn history-btn-refresh" onClick={handleRefresh} disabled={loading}>
          <span className="history-btn-icon" aria-hidden>↻</span> Refresh
        </button>
      </div>
      {error && <div className="history-error">{error}</div>}
      <div className="history-table-wrap">
        {loading ? (
          <div className="history-loading">Loading…</div>
        ) : (
          <table className="history-table">
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Exchange</th>
                <th>Market</th>
                <th>Payment</th>
                <th>Rate</th>
                <th>Side</th>
                <th>Position Size</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 ? (
                <tr><td colSpan={7} className="history-empty">No payment records found</td></tr>
              ) : (
                data.items.map((row, i) => (
                  <tr key={i}>
                    <td>{formatPaymentTime(row.payment_time)}</td>
                    <td>{row.exchange ? row.exchange.toUpperCase() : '–'}</td>
                    <td>{row.pair || '–'}</td>
                    <td className="history-amount">{row.amount != null ? formatFundingAmount(row.amount) : '–'}</td>
                    <td>{row.rate != null ? `${(Number(row.rate) * 100).toFixed(4)}%` : '–'}</td>
                    <td>{row.side || '–'}</td>
                    <td>{row.position_size != null && row.position_size !== '' ? row.position_size : '–'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {data.total > 0 && (
        <div className="history-pagination">
          <span className="history-pagination-info">
            {(page - 1) * perPage + 1}–{Math.min(page * perPage, data.total)} of {data.total}
          </span>
          <div className="history-pagination-btns">
            <button type="button" className="history-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span className="history-pagination-page">Page {page} of {totalPages}</span>
            <button type="button" className="history-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

const HISTORY_TABS = [
  { id: 'funding-payments', label: 'Funding payments' },
  { id: 'funding-payment-history', label: 'Funding Payment History' },
];

function HistoryPage({ onRefreshRef, positions }) {
  const isMobile = useIsMobile();
  const [activeSubTab, setActiveSubTab] = useState('funding-payments');

  return (
    <div className={`history-page ${isMobile ? 'history-page-mobile' : ''}`}>
      {isMobile && <h1 className="history-page-mobile-header">Funding</h1>}
      <div className="history-tabs">
        {HISTORY_TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={`history-tab-btn ${activeSubTab === id ? 'active' : ''}`}
            onClick={() => setActiveSubTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="history-content">
        {activeSubTab === 'funding-payments' && <FundingPaymentsTab onRefreshRef={onRefreshRef} positions={positions} />}
        {activeSubTab === 'funding-payment-history' && <FundingPaymentHistoryTab onRefreshRef={onRefreshRef} positions={positions} />}
      </div>
    </div>
  );
}

export default HistoryPage;
